import { Component, Inject, OnInit, PLATFORM_ID, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { PagesService } from '../pages.service';
import { NotificationsComponent } from '../../shared/notification/notification.component';
import { Product } from '../../product/product.types';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ProductsService } from '../../product/products.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { tap } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { AutocompleteInputComponent } from '../../authentication/signup/autocomplete-input/autocomplete-input.component';
import { DeliveryAddress } from '../../authentication/signup/signup.types';
import { ModalContentService } from '../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../shared/constants/modal-content-types';
import { GiftCardService } from './gift-cards.service';
import { GiftData } from './gift-card.types';
import { NotificationService } from '../../shared/notification/notification.service';
import { unableOperationMessage } from '../../shared/common/utils';
import { Session } from '../../shared/types/session.type';
import { LocalStorageService } from '../../shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from '../../shared/constants/databases';
import { SignalsStoreService } from '../../shared/signals-store.service';
import { environment } from '../../../environments/environment';
import { PurchaseTypes } from '../../shared/types/flows-config.types';
import { EmptyMessageComponent } from '../../shared/empty-message/empty-message.component';
import { Router } from '@angular/router';
import { AccountService } from '../../shared/account.service';
import { ProductCardV2Component } from '../../shared/product-card-v2/product-card-v2.component';

@Component({
  selector: 'app-gift-cards',
  imports: [
    NotificationsComponent,
    CommonModule,
    ProductCardV2Component,
    MatProgressBarModule,
    ReactiveFormsModule,
    AutocompleteInputComponent,
    EmptyMessageComponent,
  ],
  templateUrl: './gift-cards.component.html',
  styleUrl: './gift-cards.component.scss'
})
export class GiftCardsComponent implements OnInit {

  #pagesService = inject(PagesService);
  #productsService = inject(ProductsService);
  #formBuilder = inject(FormBuilder);
  #modalContentService = inject(ModalContentService);
  #giftCardService = inject(GiftCardService);
  #notificationService = inject(NotificationService);
  #localStorageervice = inject(LocalStorageService);
  #signalsStoreService = inject(SignalsStoreService);
  #router = inject(Router);
  #accountService = inject(AccountService);

  product: Signal<Product | null> = computed(() => this.#productsService.productSignal());
  selectedVariant: WritableSignal<Product | null> = signal(null);
  productCardSummary: Signal<any> = computed(() => this.setUpProductCardSummary());
  productImages: Signal<any[]> = computed(() => this.setUpProductImages());

  hasSession: Signal<boolean> = computed(() => this.#signalsStoreService.hasSession());

  giftCardForm = this.#formBuilder.group({
    senderName: ['', Validators.required],
    senderEmail: ['', [Validators.required, Validators.email]],
    recipientName: ['', [Validators.required]],
    recipientEmail: ['', [Validators.required, Validators.email]],
  });

  personalMessage = new FormControl('');
  recipientsAddress: WritableSignal<Partial<DeliveryAddress | null>> = signal(null);

  addressLabel = `Recipient's Address`

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  ngOnInit(): void {
    this.#pagesService.restartSideMenu();

    if (!isPlatformBrowser(this.platformId)) return;

    this.#giftCardService.getGiftCardData().pipe(
      tap(data => this.#productsService.productSignal.set(data)),
    ).subscribe();

    this.#setUpSessionData();
  }

  #setUpSessionData() {
    const session: Session | null = this.#localStorageervice.get(LOCALSTORAGE_KEYS.SESSION);
    if (!session) return;
    this.giftCardForm.controls.senderEmail.setValue(session.accountInfo.email);
    this.giftCardForm.controls.senderName.setValue(`${session.accountInfo.firstName} ${session.accountInfo.lastName}`);
  }

  setUpProductCardSummary() {
    const product = this.product();
    if (!product) return null;
    localStorage.setItem('selectedProductId', product.id.toString());
    return {
      productDetails: {
        ...product,
        allowVariantsWithoutStock: true
      },
      settings: {
        isSummary: true
      }
    };
  }

  private setUpProductImages() {
    const product = this.product();
    if (!product) return [];
    if (this.selectedVariant()?.img)
      return !!(this.selectedVariant()?.img || this.selectedVariant()?.additionalImages?.length) ? [this.selectedVariant()?.img, ...(this.selectedVariant()?.additionalImages || [])] : ['assets/images/product-placeholder.webp'];
    return !!(product?.img || product?.additionalImages?.length) ? [product?.img, ...product?.additionalImages] : ['assets/images/product-placeholder.webp'];
  }

  validateErrors(controlName: string): string | null {
    const control = this.giftCardForm.get(controlName);
    if (control?.valid || (!control?.dirty && !control?.touched)) return null;
    if (control?.hasError('required')) {
      return `This field is required`
    } else if (control?.hasError('email')) {
      return 'Invalid email format'
    }
    return null;
  }

  onPurchaseGiftCardClicked() {
    this.#openModalPurchase()
  }

  #openModalPurchase() {
    const giftData = this.#setUpGiftData();
    if (!giftData) return;

    const openModal = (modalType: ModalContentTypes, giftData: GiftData) => {
      this.#modalContentService.openModal(modalType, {
        title: 'Confirm Your Purchase',
        authNetPurchase: giftData
      }, {
        size: 'md',
        backdrop: 'static',
      }).closed.pipe(tap((res: { redirect: boolean }) => res?.redirect ? this.#router.navigate(['/shop']) : null)).subscribe();
    }

    if (environment.config.flows.purchase.type === PurchaseTypes.anet)
      return openModal(ModalContentTypes.AUTH_NET_PURCHASE, giftData);

    if (environment.config.flows.purchase.type === PurchaseTypes.nmi)
      return openModal(ModalContentTypes.NMI_PURCHASE, giftData);
  }

  #setUpGiftData(): GiftData | null {

    const defaultError = (isFromForm: boolean) => {
      this.#notificationService.show({ text: 'Validate mandatory fields', type: 'warning' });
      isFromForm ? this.giftCardForm.markAllAsTouched() : null;
      return null;
    }
    if (!this.giftCardForm.valid) {
      return defaultError(true);
    }

    const values = this.giftCardForm.value;

    if (!values.recipientEmail || !values.recipientName || !values.senderEmail || !values.senderName) {
      return defaultError(true);
    }

    const variant = this.selectedVariant() ?? null;
    if (!variant) {
      unableOperationMessage(this.#modalContentService);
      return null;
    }

    const { variantId, name: variantName, price: variantPrice } = variant;

    if (!this.recipientsAddress()) {
      this.#notificationService.show({ text: 'You should select a valid address', type: 'warning' });
      return null;
    }

    return {
      ...values,
      personalMessage: this.personalMessage.value ?? '',
      variantId,
      variantName,
      variantPrice
    } as GiftData
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  validateEmailExistence() {
    if (this.hasSession()) return;
    const email = this.giftCardForm.controls.senderEmail.value ?? '';
    if (!email) return;
    this.#accountService.validateEmailExistence(email).pipe(tap(emailExists => emailExists ? this.#openModalEmailExits(email) : null)).subscribe();
  }

  #openModalEmailExits(email: string) {
    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Account Already Exists',
      textContent: `It looks like an account is already associated with this email <b>(${email})</b>. Would you like to sign in to continue or try again with a different email?`,
      confirmButtonText: 'Sign In',
      cancelButtonText: 'Try a Different Email',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (res?.confirm) {
          this.#router.navigate(['/login']);
          this.#localStorageervice.set(LOCALSTORAGE_KEYS.ROUTE_TO_NAVIGATE_AFTER_LOGIN, '/pages/gift-cards');
        } else {
          this.giftCardForm.controls.senderEmail.setValue('');
        }
      })
  }

}